import { useState, useCallback, useMemo } from 'react';
import type { FormData, EmbedFormOptions, GenericObjectType } from '@/types';
import useSubmitForm from '@/hooks/use-submit-form';
import { buildId } from '@/utils';
import useFormSpecs from '@/hooks/use-form-specs';
import LoadingOverlay from '@/components/loading-overlay/loading-overlay';
import {
  onSuccessDefault,
  onErrorDefault,
  mapFormErrorsToExtraErrors,
  onNextStepDefault,
} from './yepcode-form.utils';
import { processFormSpecs } from '@/utils/process-form-specs';
import {
  YepCodeJsonSchemaForm,
  YepCodeJsonSchemaFormProps,
} from '../json-schema-form';
import classNames from 'classnames';

const DEFAULT_FORM_OPTIONS: EmbedFormOptions = {
  theme: 'dark',
  loadingOverlayDisabled: false,
  loadingOverlayContent: 'Sending information...',
  locale: 'en',
};

export type YepCodeFormOptions = Omit<EmbedFormOptions, 'withBranding'>;

export interface YepCodeFormProps
  extends Omit<YepCodeJsonSchemaFormProps, 'onError' | 'schema'> {
  /**
   * The host URL for YepCode (optional).
   */
  hostUrl?: string;
  /**
   * Headers to be added to form fetch and submit requests.
   */
  headers?: GenericObjectType;
  /**
   * The YepCode team slug.
   */
  team: string;
  /**
   * The YepCode process ID.
   */
  processId: string;
  /**
   * Indicates whether the form should be submitted asynchronously (optional).
   */
  async?: boolean;
  /**
   * A function called when the form is successfully submitted.
   * Receives the following parameters:
   * - formId: The ID of the form.
   * - processExecutionResult: The result of the process execution as a generic object.
   * - formSubmittedData: The data submitted with the form.
   */
  onSuccess?: (
    formId: string,
    processExecutionResult: GenericObjectType,
    formSubmittedData: FormData
  ) => void;
  /**
   * A function called when the next step of the form is called.
   */
  onNextStep?: (
    formId: string,
    previousProcessExecutionResult: GenericObjectType
  ) => void;
  /**
   * A function called when an error occurs during form submission.
   * Receives the following parameters:
   * - formId: The ID of the form.
   * - error: The error that occurred, of unknown type.
   * - formSubmittedData: The data submitted with the form.
   */
  onError?: (
    formId: string,
    error: unknown,
    formSubmittedData: FormData
  ) => void;
  /**
   * The default values for the form.
   */
  defaultValues?: FormData;
  /**
   * The options for YepCode form.
   */
  options: YepCodeFormOptions;
}

export interface YepCodeFormStep {
  processId: string;
  data: FormData;
  result: GenericObjectType;
}
export interface YepCodeFormState {
  processId: string;
  steps: YepCodeFormStep[];
  variables: GenericObjectType;
}

const YepCodeForm = ({
  hostUrl = 'https://cloud.yepcode.io',
  headers,
  team,
  processId,
  onNextStep = onNextStepDefault,
  onSuccess = onSuccessDefault,
  onError = onErrorDefault,
  defaultValues = {},
  async = false,
  disabled = false,
  readonly = false,
  options,
  ...rest
}: YepCodeFormProps) => {
  const [currentProcess, setCurrentProcess] = useState<YepCodeFormState>({
    processId,
    steps: [],
    variables: {
      ...(options && options.variables),
    },
  });

  const [submitForm, { loading: submitting }] = useSubmitForm({
    hostUrl,
    headers,
    team,
    processId: currentProcess.processId,
    async,
  });

  const formId = useMemo(() => {
    const formId = buildId();
    console.log(`Building YepCode form with id ${formId}`);
    return formId;
  }, [team, currentProcess]);

  const initialValues = useMemo(() => {
    const values = defaultValues || {};
    return window.location.search
      ? {
          ...values,
          ...Object.fromEntries(
            new URLSearchParams(window.location.search).entries()
          ),
        }
      : values;
  }, [defaultValues]);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialValues);
  const [extraErrors, setExtraErrors] = useState({});

  const onSubmit = useCallback(
    (form: any) => {
      setExtraErrors({});
      setLoading(true);
      setFormData(form.formData);
      submitForm({
        metadata: {
          origin: {
            url: window.location.href,
            browser: {
              language: navigator.language,
              userAgent: navigator.userAgent,
            },
          },
          ...(currentProcess.steps.length > 0 && {
            steps: currentProcess.steps,
          }),
          ...(Object.keys(currentProcess.variables).length > 0 && {
            variables: currentProcess.variables,
          }),
          ...(options && options.locale && { locale: options.locale }),
        },
        ...form.formData,
      })
        .then((processExecutionResult) => {
          setFormData(initialValues);
          if (processExecutionResult.nextProcessId) {
            const steps = currentProcess.steps;
            steps.push({
              processId: currentProcess.processId,
              data: form.formData,
              result: processExecutionResult,
            });
            setCurrentProcess({
              processId: processExecutionResult.nextProcessId,
              steps: steps,
              variables: {
                ...currentProcess.variables,
                ...processExecutionResult.variables,
              },
            });
            onNextStep(
              processExecutionResult.nextProcessId,
              processExecutionResult
            );
            return;
          }
          onSuccess(formId, processExecutionResult, form.formData);
        })
        .catch((error) => {
          if (error?.formErrors) {
            setExtraErrors(mapFormErrorsToExtraErrors(error?.formErrors));
          } else {
            onError(formId, error, form.formData);
          }
        })
        .finally(() => setLoading(false));
    },
    [formId, initialValues, onSuccess, onError, submitForm, currentProcess]
  );

  const { loading: loadingFormSpecs, formSpecs } = useFormSpecs({
    hostUrl,
    headers,
    team,
    processId: currentProcess.processId,
  });

  if (loadingFormSpecs || !formSpecs) {
    return null;
  }

  formSpecs.schema.embedFormOptions = {
    ...DEFAULT_FORM_OPTIONS,
    ...formSpecs.schema.embedFormOptions,
    ...options,
    variables: {
      ...currentProcess.variables,
      ...(options && options.variables),
    },
  };

  const processedFormSpecs = processFormSpecs(formSpecs);

  const propertyKeys = Object.keys(processedFormSpecs.schema.properties || {});
  // Remove the formData entries not present on formSchema
  const onlyValidPropertiesFormData = Object.fromEntries(
    Object.entries(formData).filter(([key, value]) =>
      propertyKeys.includes(key)
    )
  );

  const {
    loadingOverlayDisabled,
    loadingOverlayContent,
    locale,
    fallbackLocale,
  } = processedFormSpecs.embedFormOptions;
  return (
    <div
      className={classNames([
        'yepcode-form-wrapper',
        processedFormSpecs.embedFormOptions.className,
      ])}
      data-testid="yepcode-form"
    >
      <LoadingOverlay
        active={loading}
        content={loadingOverlayContent}
        disabled={loadingOverlayDisabled}
      >
        <div className="yepcode-form-outer">
          <YepCodeJsonSchemaForm
            id={formId}
            schema={processedFormSpecs.schema}
            uiSchema={processedFormSpecs.uiSchema}
            embedFormOptions={processedFormSpecs.embedFormOptions}
            locale={locale}
            fallbackLocale={fallbackLocale}
            formData={onlyValidPropertiesFormData}
            onSubmit={onSubmit}
            noHtml5Validate
            disabled={disabled}
            readonly={readonly}
            showErrorList={false}
            extraErrors={extraErrors}
            {...rest}
          />
        </div>
      </LoadingOverlay>
    </div>
  );
};

YepCodeForm.displayName = 'YepCodeForm';

export default YepCodeForm;
